import {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { loader } from 'graphql.macro';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { useAuth0 } from './Auth0Provider';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { Navigate } from 'react-router-dom';
import CustomSpinner from '../components/CustomSpinner';
import { useUserIndexedDB } from '../hooks/offline-hooks/userMutationIndexedDB';
import pjson from '../../package.json';
import UserNotFound from '../pages/UserNotFound';
import {
  customTaskAssessmentType,
  getAssessmentTypesByEditable,
  qualityTemplateTypes,
} from '../constants/strings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { observationClassificationTypes } from '../utilities/observations';
import { getBrowserLanguage } from '../i18n';
import usePreloadImages from '../hooks/usePreloadImages';
import { defaultHourCodes } from '../components/activities/activites';
import { expenseTopInputs } from '../pages/hr/Expenses';

const userQuery = loader('./UserProvider.graphql');
const UserContext = createContext();
const updateUserMutation = loader('src/graphql/mutations/user_update.graphql');
const rolesQuery = loader('./UserProvider.roles.graphql');

const packageVersion = pjson.version.split('.');
const currentDbName = `userSpecificMutation-${packageVersion[0]}.${packageVersion[1]}`;
const EMPTY_ROLES = [];

export const UserProvider = ({ children }) => {
  const { enableQualityModule } = useFlags();
  const [user, setUser] = useState(null);

  const { saveUserInfo } = useUserIndexedDB(currentDbName);
  const { isAuthenticated, isLoading } = useAuth0();
  const isOnline = useOnlineStatus();
  const [updateUser] = useMutation(updateUserMutation);
  const params = new URLSearchParams();
  const p = params.get('sync');
  const syncing = useMemo(() => {
    return p === 'TRUE';
  }, [p]);
  const { refetch, networkStatus } = useQuery(userQuery, {
    skip: !isAuthenticated || syncing,
    fetchPolicy: isOnline ? 'network-only' : 'cache-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (res) => setUser(res?.user),
  });

  const loading = networkStatus === NetworkStatus.loading;

  useEffect(() => {
    if (!indexedDB.databases) return;
    indexedDB.databases().then((dbs) => {
      dbs
        .filter(
          (db) =>
            db.name?.startsWith('userSpecificMutation-') &&
            db.name !== currentDbName,
        )
        .forEach((db) => indexedDB.deleteDatabase(db.name));
    });
  }, []);

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem('user', JSON.stringify(user));
      saveUserInfo(user);
      let newOptions = {};
      if (!user.frontVersion && isOnline) {
        newOptions = { ...newOptions, frontVersion: `${pjson.version}` };
      }
      const browser = getBrowserLanguage();
      const browserLocale = browser?.startsWith('fr') ? 'fr-CA' : 'en';
      if (user?.locale !== browserLocale) {
        newOptions = { ...newOptions, locale: browserLocale };
      }

      // Kept in sync the same way as locale above — always follows the
      // browser, so reports and any other time-aware content stay in the
      // user's actual local time even if they travel
      let browserTimeZone;
      try {
        browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        browserTimeZone = null;
      }
      if (browserTimeZone && user?.timeZone !== browserTimeZone) {
        newOptions = { ...newOptions, timeZone: browserTimeZone };
      }

      if (Object.keys(newOptions)?.length > 0) {
        updateUser({ variables: { ...newOptions } });
      }
    }
  }, [user, saveUserInfo, updateUser, isOnline]);

  usePreloadImages([user?.avatarUrl]);

  const { data: { roles: allRoles = EMPTY_ROLES } = {} } = useQuery(
    rolesQuery,
    {
      variables: {
        options: { sort: [{ field: 'title', order: 'asc' }] },
      },
      fetchPolicy: 'cache-first',
    },
  );

  const contextValue = useMemo(() => {
    const isAdmin = user?.role === 'ADMIN';
    const team = user?.team || [];
    const isSupervisor = !!team?.length;
    const incidentName = user?.company?.incidentName;
    const observationName = user?.company?.observationName;
    const assessmentName = user?.company?.assessmentName;
    const timecardCodes = user?.company?.timecardCodes || defaultHourCodes;
    const expenseCodes =
      user?.company?.expenseCodes ||
      expenseTopInputs?.find((i) => i.query === 'type')?.options;
    const toolboxName = user?.company?.toolboxName;
    const companyTestOptions = user?.company?.testQuestionOptions;
    const { company = {} } = user || {};
    const isSupport = !!user?.isSupport;

    const activeTemplateTypes = Object.fromEntries(
      Object.entries(company?.templateTypes || {}).filter(
        ([, value]) => value?.active,
      ),
    );

    const defaultEditableTypes = getAssessmentTypesByEditable(true);
    const nonEditableTypes = getAssessmentTypesByEditable(false);

    //types allowed for new templates (selected by user)
    const assessmentTypes = {
      ...nonEditableTypes,
      ...activeTemplateTypes,
    };

    const sortedAssessmentTypes = Object.fromEntries(
      Object.entries(assessmentTypes).sort(([a], [b]) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    );

    //all template types that have been created so we don't lose the icon and title
    const allTemplateTypes = {
      ...customTaskAssessmentType,
      ...nonEditableTypes,
      ...(company?.templateTypes || {}),
    };

    const assetTypes = company?.assetTypes || {};

    const currentUser = localStorage.getItem('user');

    const nonQualityAssessments = Object.fromEntries(
      Object.entries(assessmentTypes).filter(
        ([, value]) => !qualityTemplateTypes.includes(value?.key),
      ),
    );

    const customObservationClassificationTypes =
      company?.observationClassifications?.split('|') ||
      observationClassificationTypes;

    const companyLocations = !!user?.company?.locations
      ? user.company.locations?.split('|')
      : [];

    return {
      user: user || currentUser,
      isAdmin,
      team,
      isSupervisor,
      incidentName,
      assessmentName,
      observationName,
      defaultObservationWorkspaceId: user?.company?.observationWorkspaceId,
      toolboxName,
      company,
      assessmentTypes: !enableQualityModule
        ? nonQualityAssessments
        : sortedAssessmentTypes,
      allTemplateTypes,
      defaultEditableTypes,
      activeTemplateTypes,
      isSupport,
      assetTypes,
      customObservationClassificationTypes,
      refetchUser: refetch,
      syncing,
      companyLocations,
      timecardCodes,
      expenseCodes,
      companyTestOptions,
      allRoles,
    };
  }, [user, enableQualityModule, refetch, syncing, allRoles]);

  if (!syncing && isOnline && (isLoading || loading)) {
    return <CustomSpinner text={'Loading User...'} />;
  }

  const localStorageUser = localStorage.getItem('user');
  const cachedUser = localStorageUser ? JSON.parse(localStorageUser) : null;

  if (!isAuthenticated) {
    // Offline, Auth0 has no network to validate/refresh the session. If we
    // already have a previously authenticated user cached, keep them in the
    // app rather than bouncing to /login — Auth0 re-validates for real as
    // soon as we're back online.
    if (isOnline || !cachedUser?.id) {
      return <Navigate to="/login" />;
    }
  }

  if (!user?.id && cachedUser?.id) {
    setUser(cachedUser);
  }

  if (!user?.id) {
    if (isOnline && !isLoading && !loading && isAuthenticated) {
      return <UserNotFound />;
    }
    return <CustomSpinner text={'Loading User...'} />;
  }

  return (
    <Suspense>
      <UserContext.Provider value={contextValue}>
        {children}
      </UserContext.Provider>
    </Suspense>
  );
};

export const useCurrentUser = () => useContext(UserContext);
